import React, { useCallback, useEffect, useMemo, useState } from "react";
import { v4 as uuid } from "uuid";
import { Props, State } from "./interfaces";
import { EmployeeSurveyItemRef } from "../../models/employee-survey-items/employee-survey-item-ref";
import EditEmployeeSurveyFields from "./EditEmployeeSurveyFields";
import { Button, Dialog, DialogContent, Grid, Typography } from "@mui/material";
import { EmployeeSurveysApiService } from "../../services/employee-surveys/employee-surveys-api.service";
import { EmployeeSurveyStatuses } from "../../utils/constants/constants";
import { EmployeeSurveyStatusRef } from "../../models/employee-survey-status/employee-survey-status-ref";
import DialogTitle from "../DialogTitle/DialogTitle";
import DialogActions from "../DialogActions/DialogActions";
import ConfirmationChangesDialog from "../ConfirmationChangesDialog/ConfirmationChangesDialog";
import { Container } from "./styles";
import { useTranslation } from "react-i18next";
import LoadingOverlay from "../LoadingOverlay/LoadingOverlay";

const EditEmployeeSurvey = ({ item, handleNotify, children }: Props) => {
  const { t } = useTranslation();

  const INITIAL_STATE: State = useMemo(
    () => ({
      open: false,
      item: item,
      loading: false,
      error: "",
      CCDOpen: false,
    }),
    [item]
  );

  const [state, setState] = useState({ ...INITIAL_STATE });

  //#region save
  const handleSave = useCallback(
    async (isAuto?: boolean, submit?: boolean) => {
      try {
        setState((state) => ({ ...state, loading: true }));
        const response = await new EmployeeSurveysApiService().saveItems([
          {
            ...state.item,
            status: submit
              ? (EmployeeSurveyStatuses.Submitted as EmployeeSurveyStatusRef)
              : (EmployeeSurveyStatuses.Draft as EmployeeSurveyStatusRef),
          },
        ]);
        if (response.error) {
          setState((state) => ({
            ...state,
            error: response.error,
          }));
        } else {
          if (!isAuto) {
            setState((state) => ({ ...state, open: false }));
            handleNotify();
          }
        }
      } catch (error) {
        setState((state) => ({
          ...state,
          error: error,
        }));
      } finally {
        setState((state) => ({ ...state, loading: false }));
      }
    },
    [state.item, handleNotify, INITIAL_STATE, item]
  );
  //#endregion

  //#region change item
  const handleChangeSurveyItem = useCallback(
    (
      surveyItem: EmployeeSurveyItemRef,
      surveyItemIdx: number,
      field: keyof EmployeeSurveyItemRef,
      value: EmployeeSurveyItemRef[keyof EmployeeSurveyItemRef]
    ) => {
      setState((state) => ({
        ...state,
        item: {
          ...state.item,
          surveyItems: [
            ...state.item.surveyItems.map((_item, idx) =>
              surveyItem.id === _item.id ? { ...surveyItem, [field]: value } : _item
            ),
          ],
        },
      }));
    },
    []
  );
  //#endregion

  //#region generate survey items (When editing first time)
  const generateItems = useCallback(() => {
    if (item.employeeSurveyConfiguration) {
      setState((state) => ({
        ...state,
        item: {
          ...state.item,
          surveyItems: [
            ...(item.employeeSurveyConfiguration?.surveyItems.map(
              (_item) =>
                ({
                  id: uuid(),
                  isDeleted: false,
                  item: _item,
                  textAnswer: null,
                  polarAnswer: null,
                  ranking: _item.type?.ranking ? 0 : null,
                  predefinedAnswer: null,
                } as EmployeeSurveyItemRef)
            ) || []),
          ],
        },
      }));
    }
  }, [item, state.item.surveyItems]);
  //#endregion

  useEffect(() => {
    if (state.item.status?.id === EmployeeSurveyStatuses.Pending.id && state.item.surveyItems.length === 0)
      generateItems();
  }, [state.item.surveyItems, state.item.status?.id]);

  //#region validate items
  const validateItems = useCallback((): boolean => {
    const requiredItems = state.item.surveyItems.filter((surveyItem) => surveyItem.item?.isRequired);

    const areAllItemsFilled = requiredItems.find(
      (requiredItem) =>
        requiredItem.polarAnswer === null &&
        requiredItem.predefinedAnswer === null &&
        (requiredItem.ranking === null || requiredItem.ranking === 0) &&
        (requiredItem.textAnswer === null || requiredItem.textAnswer.trim().length === 0)
    );

    return !areAllItemsFilled;
  }, [state.item]);

  return (
    <>
      <Grid
        onClick={() => setState((state) => ({ ...state, open: true }))}
        style={{
          cursor: "pointer",
        }}
      >
        {children}
      </Grid>
      <Dialog
        open={state.open}
        maxWidth={"lg"}
        fullWidth
      >
        <DialogTitle
          title={item.employeeSurveyConfiguration?.name ?? ""}
          handleClose={() => setState((state) => ({ ...state, CCDOpen: true }))}
        />
        <DialogContent>
          <LoadingOverlay
            error={state.error}
            loading={state.loading}
          >
            <Container>
              <EditEmployeeSurveyFields
                item={state.item}
                handleChangeSurveyItem={handleChangeSurveyItem}
              />
            </Container>
          </LoadingOverlay>
        </DialogContent>

        <DialogActions
          handleCancel={() => setState((state) => ({ ...state, CCDOpen: true }))}
          handleSave={() => (validateItems() ? handleSave(false, true) : alert("Please fill all required fields"))}
          customSaveName={`${t(`buttons.submit`)}`}
        >
          <Button
            onClick={() => handleSave(false, false)}
            variant={"contained"}
          >
            {`${t(`buttons.save_as_draft`)}`}
          </Button>
        </DialogActions>
      </Dialog>
      <ConfirmationChangesDialog
        open={state.CCDOpen}
        handleCancel={() => setState((state) => ({ ...state, CCDOpen: false }))}
        handleDiscard={() => setState({ ...INITIAL_STATE })}
      />
    </>
  );
};

export default EditEmployeeSurvey;
